/**
 * Synchro SANS serveur Klavox (R26 / G34) : le coffre chiffre est depose sur
 * le WebDAV/Nextcloud **que l'utilisateur possede deja**, et repris de la
 * meme facon sur ses autres appareils. Reference : Enpass, KeePass.
 *
 * Ce que Klavox gagne : aucune infrastructure a heberger pour synchroniser.
 * Ce que cela impose, et qui est la raison d'etre de ce module :
 *
 *  - **Ciphertext-only.** Ce qui part est EXACTEMENT un export Klavox
 *    (`vault-export.ts`, `docs/ARCHITECTURE.md` §4) : ce que verrait deja le
 *    serveur souverain, jamais un secret en clair. Le WebDAV est un serveur
 *    comme un autre -- sauf qu'il n'est meme pas le notre.
 *  - **Les identifiants voyagent dans un EN-TETE**, jamais dans l'URL : une
 *    URL finit dans les journaux d'acces de chaque intermediaire. Le mot de
 *    passe d'application n'est ni journalise, ni insere dans un message
 *    d'erreur, ni persiste (voir `app.ts` : URL et identifiant seulement).
 *  - **Echec franc.** Un WebDAV mal authentifie renvoie une PAGE HTML de
 *    connexion, pas une erreur JSON. La prendre pour « coffre distant vide »
 *    ferait ecraser le coffre des autres appareils par celui-ci : c'est la
 *    perte de donnees la plus facile a produire ici. Seul un 404 franc vaut
 *    « rien la-bas ».
 *  - **Le distant n'est pas fiable** : le lot telecharge est authentifie
 *    (AAD, `authenticateVaultRecords`) AVANT toute fusion -- `authenticate`
 *    est obligatoire, pas optionnel.
 *  - **Pas d'ecrasement aveugle** : `If-Match` sur l'ETag recu (`If-None-Match: *`
 *    quand le fichier n'existe pas). Un 412 signifie qu'un autre appareil a
 *    ecrit entre-temps -> on refusionne, on ne pietine pas.
 *
 * La fusion elle-meme n'est PAS refaite ici : c'est `conflict.ts` via
 * `mergeVaultExport`, la meme resolution que la synchro serveur.
 */
import { normalizeHttpsEndpoint } from './https-endpoint.js';
import {
  buildVaultExport,
  mergeVaultExport,
  parseVaultExport,
  serializeVaultExport,
  type MergeImportResult,
  type VaultExportFile,
} from './vault-export.js';
import type { AccountEnvelope } from './types.js';
import type {
  EncryptedVaultAttachment,
  EncryptedVaultFolder,
  EncryptedVaultItem,
} from '@klavox/vault-model';

export type WebdavSyncErrorCode =
  | 'config'
  /** Identifiants refuses, ou reponse de connexion HTML a la place du fichier. */
  | 'auth'
  | 'network'
  /** Un autre appareil a ecrit entre le telechargement et le depot. */
  | 'conflict'
  | 'server'
  /** Contenu distant illisible ou altere. */
  | 'payload';

export class WebdavSyncError extends Error {
  readonly code: WebdavSyncErrorCode;

  constructor(code: WebdavSyncErrorCode, message: string) {
    super(message);
    this.name = 'WebdavSyncError';
    this.code = code;
  }
}

/** Nom du fichier depose dans le dossier WebDAV indique. */
export const WEBDAV_VAULT_FILENAME = 'klavox-vault.json';

export interface WebdavConfig {
  /** Dossier WebDAV, normalise par `normalizeWebdavFolderUrl`. */
  readonly folderUrl: string;
  readonly username: string;
  /** Mot de passe d'application. SECRET : en-tete uniquement, jamais persiste. */
  readonly password: string;
}

/**
 * Valide le dossier WebDAV saisi. Meme validateur que le reste de la galaxie
 * (HTTPS obligatoire, ni identifiants ni parametres dans l'URL) plus une
 * exigence propre a WebDAV : un CHEMIN. Deposer a la racine d'un domaine
 * n'est jamais ce que l'utilisateur veut et trahit une URL copiee de travers.
 */
export function normalizeWebdavFolderUrl(raw: string): string {
  const normalized = normalizeHttpsEndpoint(raw, {
    label: 'WebDAV',
    fail: (message) => new WebdavSyncError('config', message),
  });
  if (new URL(normalized).pathname.replace(/\/+$/u, '') === '') {
    throw new WebdavSyncError(
      'config',
      "L'URL WebDAV doit designer un dossier (ex. /remote.php/dav/files/vous/Klavox)",
    );
  }
  return normalized;
}

/** URL complete du fichier de coffre. */
export function webdavVaultUrl(folderUrl: string): string {
  return `${folderUrl.replace(/\/+$/u, '')}/${WEBDAV_VAULT_FILENAME}`;
}

/** Identifiants en en-tete : ils ne doivent JAMAIS atteindre l'URL. */
function authHeader(config: WebdavConfig): string {
  const raw = `${config.username}:${config.password}`;
  // btoa ne connait que le latin-1 : passer par UTF-8 avant d'encoder.
  const utf8 = Array.from(new TextEncoder().encode(raw), (byte) => String.fromCharCode(byte)).join('');
  return `Basic ${btoa(utf8)}`;
}

export interface RemoteVaultSnapshot {
  /** `null` quand le dossier ne contient pas encore de coffre (404 franc). */
  readonly file: VaultExportFile | null;
  /** Version distante, a renvoyer en `If-Match` pour ne rien ecraser. */
  readonly etag: string | null;
}

function looksLikeHtml(body: string): boolean {
  const head = body.trimStart().slice(0, 200).toLowerCase();
  return head.startsWith('<!doctype html') || head.startsWith('<html') || head.startsWith('<?xml');
}

async function call(url: string, init: RequestInit, fetchImpl: typeof fetch): Promise<Response> {
  try {
    return await fetchImpl(url, { ...init, credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'no-store' });
  } catch {
    // Le message de la cause n'est pas repris : il peut contenir l'URL, donc
    // potentiellement ce que l'utilisateur y aurait colle par erreur.
    throw new WebdavSyncError('network', 'Serveur WebDAV injoignable');
  }
}

/** Telecharge le coffre distant. 404 = absent ; tout le reste doit etre franc. */
export async function fetchRemoteVault(
  config: WebdavConfig,
  fetchImpl: typeof fetch = fetch,
): Promise<RemoteVaultSnapshot> {
  const response = await call(
    webdavVaultUrl(config.folderUrl),
    { method: 'GET', headers: { authorization: authHeader(config) } },
    fetchImpl,
  );
  if (response.status === 404) return { file: null, etag: null };
  if (response.status === 401 || response.status === 403) {
    throw new WebdavSyncError('auth', 'Identifiants WebDAV refuses');
  }
  if (!response.ok) {
    throw new WebdavSyncError('server', `Le serveur WebDAV a repondu ${response.status}`);
  }
  const text = await response.text();
  // Un WebDAV non authentifie sert sa page de connexion en 200. La lire comme
  // « coffre vide » ecraserait les autres appareils : refus franc.
  if (looksLikeHtml(text)) {
    throw new WebdavSyncError(
      'auth',
      'Le serveur a renvoye une page web, pas le coffre : verifiez l’URL et les identifiants',
    );
  }
  let file: VaultExportFile;
  try {
    file = parseVaultExport(text);
  } catch (cause) {
    throw new WebdavSyncError('payload', cause instanceof Error ? cause.message : 'coffre distant illisible');
  }
  return { file, etag: response.headers.get('etag') };
}

/**
 * Depose le coffre. `etag` = version telechargee juste avant (`null` = on
 * croyait le fichier absent) : le serveur refuse l'ecriture (412) si un autre
 * appareil a ecrit entre-temps.
 */
export async function putRemoteVault(
  config: WebdavConfig,
  file: VaultExportFile,
  etag: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  const precondition: Record<string, string> =
    etag === null ? { 'if-none-match': '*' } : { 'if-match': etag };
  const response = await call(
    webdavVaultUrl(config.folderUrl),
    {
      method: 'PUT',
      headers: {
        authorization: authHeader(config),
        'content-type': 'application/json',
        ...precondition,
      },
      body: serializeVaultExport(file),
    },
    fetchImpl,
  );
  if (response.status === 401 || response.status === 403) {
    throw new WebdavSyncError('auth', 'Identifiants WebDAV refuses');
  }
  if (response.status === 412 || response.status === 409) {
    throw new WebdavSyncError('conflict', 'Le coffre distant a change entre-temps');
  }
  if (!response.ok) {
    throw new WebdavSyncError('server', `Le serveur WebDAV a repondu ${response.status}`);
  }
  return response.headers.get('etag');
}

export interface WebdavSyncInput {
  readonly config: WebdavConfig;
  readonly account: AccountEnvelope;
  readonly local: {
    readonly items: readonly EncryptedVaultItem[];
    readonly folders: readonly EncryptedVaultFolder[];
    readonly attachments?: readonly EncryptedVaultAttachment[];
  };
  /**
   * Compte les enregistrements distants ALTERES (typiquement
   * `vaultStore.authenticateExternal(file).rejected`). Obligatoire : le
   * WebDAV n'est pas plus fiable que le serveur souverain.
   */
  readonly authenticate: (file: VaultExportFile) => number;
  readonly fetchImpl?: typeof fetch;
  readonly now?: number;
}

export interface WebdavSyncResult {
  readonly merged: MergeImportResult;
  readonly conflicts: number;
  /** Vrai si un coffre existait deja la-bas (donc une vraie fusion). */
  readonly hadRemote: boolean;
  readonly etag: string | null;
}

/**
 * Un tour complet : telecharger, authentifier, fusionner (`conflict.ts`),
 * redeposer. En cas de 412 (ecriture concurrente d'un autre appareil), on
 * recommence le tour -- une seule fois, puis on rend la main : boucler
 * indefiniment contre un appareil plus rapide n'aide personne.
 *
 * ponytail: un seul fichier pour tout le coffre (lisible, atomique cote
 * WebDAV). Si un coffre volumineux rend le depot trop lourd, decouper par
 * enregistrement -- `planRecordTransfer` de `@klavox/vault-model` est fait
 * pour ca.
 */
export async function syncVaultWithWebdav(input: WebdavSyncInput): Promise<WebdavSyncResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  let lastError: WebdavSyncError | null = null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const snapshot = await fetchRemoteVault(input.config, fetchImpl);
    if (snapshot.file !== null && input.authenticate(snapshot.file) > 0) {
      throw new WebdavSyncError('payload', 'coffre distant altere ou issu d’un autre compte : fusion refusee');
    }
    const merged: MergeImportResult =
      snapshot.file === null
        ? {
            items: input.local.items,
            folders: input.local.folders,
            attachments: input.local.attachments ?? [],
            itemConflicts: 0,
            folderConflicts: 0,
          }
        : mergeVaultExport(input.local, snapshot.file);
    const file = buildVaultExport({
      account: input.account,
      items: merged.items,
      folders: merged.folders,
      attachments: merged.attachments,
      now: input.now,
    });
    try {
      const etag = await putRemoteVault(input.config, file, snapshot.etag, fetchImpl);
      return {
        merged,
        conflicts: merged.itemConflicts + merged.folderConflicts,
        hadRemote: snapshot.file !== null,
        etag,
      };
    } catch (cause) {
      if (!(cause instanceof WebdavSyncError) || cause.code !== 'conflict') throw cause;
      lastError = cause;
    }
  }
  throw lastError ?? new WebdavSyncError('conflict', 'Le coffre distant a change entre-temps');
}
